import React, { lazy, useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate
} from 'react-router-dom';
import { useQuery } from '@apollo/client';
import PubSub from 'pubsub-js';

import { yotpo, attentive } from '../util/config';
import { applyNewCustomerCoupon } from '../util/util';
import * as Styled from './App.styled';
import AuthenticationProvider, {
  STEPS
} from './Authentication/Authentication.context';
import { isPreview } from '../graphql/previews';

import { AlertbarWithData } from './Alertbar/Alertbar';
import HeaderWithData from './Header/Header';
import Footer from './Footer/Footer';
import Loader from './Loader/Loader';
import LoadingViewContainer from './LoadingView/LoadingView';
import AuthenticationDialog from './Authentication/components/AuthenticationDialog/AuthenticationDialog';
import CartDialog from './Cart/components/CartDialog';
import CouponContainer from './CouponContainer/CouponContainer';
import AccountNavDialog from './Account/components/AccountNavDialog';
import LoopNotification from './LoopNotification/LoopNotification';
import BreakpointProvider from '../util/Breakpoint.context';
import { track } from '../util/optimizely';
import { trackEvent as dataLayerPush } from '../util/track';
import RemoveScrollProvider from './RemoveScroll/RemoveScroll.context';
import { SITE_CONFIG } from './App.gql';
import { initializeLoopOnStore, getLoopParams } from '../util/loopReturns';
import { useIdentity } from './Authentication/Authentication.hooks';

import '../styles/global/main.scss';

const Cart = lazy(() => import('./Cart'));
const Authentication = lazy(() => import('./Authentication'));
const { ACTIVATE, LOGIN, FORGOT } = STEPS;
const Account = lazy(() => import('./Account/Account'));
const AccountSettings = lazy(() =>
  import('./Account/components/AccountSettings/AccountSettings')
);
const AccountOverview = lazy(() =>
  import('./Account/components/AccountOverview/AccountOverview')
);
const AccountDetails = lazy(() =>
  import('./Account/components/AccountDetails/AccountDetails')
);
const OrderHistory = lazy(() =>
  import('./Account/components/OrderHistory/OrderHistory')
);
const OrderDetails = lazy(() =>
  import('./Account/components/OrderDetails/OrderDetails')
);
const SavedAddresses = lazy(() =>
  import('./Account/components/SavedAddresses/SavedAddresses')
);
const NotFoundPage = lazy(() => import('./NotFoundPage/NotFoundPage'));

const urlParams = new URLSearchParams(window.location.search);

/**
 * Top-level component for rendering Bombas.com.
 */
const App = (config) => {
  const [isPlainPage, setIsPlainPage] = useState(false);
  const { identifyUser } = useIdentity();
  const { data } = useQuery(SITE_CONFIG, {
    variables: { key: 'site', preview: isPreview('settings') }
  });
  const siteConfig = data?.settings[0]?.values;
  const loopParams = getLoopParams();
  const [viewCouponDialogDisplay, setViewCouponDialogDisplay] =
    useState('hidden');

  // expose email validation & coupon application for Attentive
  if (siteConfig?.coupon_code && window.Bombas) {
    window.Bombas.applyNewCustomerCoupon = applyNewCustomerCoupon.bind(
      null,
      siteConfig.coupon_code.value
    );
  }

  useEffect(() => {
    track('Page_view');

    /**
     * Pass yotpo app key to GTM
     */
    dataLayerPush({ shop: { yotpoAppKey: yotpo, attentiveKey: attentive } });

    // identify user via url param if present
    const email = urlParams.get('email');
    if (email) {
      identifyUser({ email });
      dataLayerPush({
        event: 'identifyEvent',
        customer: { email }
      });
    }

    /**
     * When a page is loaded from bfcache, the cart data is sometimes stale
     * This snippet ensures that the cart data is always refreshed when the page is loaded from cache
     *
     * @see @link https://web.dev/bfcache/
     */
    window.addEventListener('pageshow', (event) => {
      if (event.persisted) {
        PubSub.publish('cart.update');
      }
    });
  }, []);

  // If Loop parameters are sent in the request, we will initialize the Loop "On Store" experience
  useEffect(() => {
    (async () => {
      await initializeLoopOnStore();
    })();
  }, []);

  /**
   * When react-router hits a catch-all route, this component renders the 404 page if its truly a 404
   *
   * Otherwise it renders a fragment and is managed outside of React
   *
   * @returns {ReactNode | null}
   */
  const NotFoundPageWrapper = () => {
    const [pageHas404Container] = useState(
      // Shopify puts the classname `template-404` on the body when its a 404
      document.querySelector('body.template-404')
    );

    useEffect(() => {
      if (!pageHas404Container) setIsPlainPage(true);
    }, [pageHas404Container]);

    if (pageHas404Container) return <NotFoundPage />;

    return <></>;
  };

  return (
    <BreakpointProvider>
      <AuthenticationProvider>
        <RemoveScrollProvider>
          <Router>
            <AlertbarWithData />
            <HeaderWithData />
            <Styled.ContentContainer plainPage={isPlainPage}>
              <Loader fallback={<LoadingViewContainer />}>
                <Routes>
                  <Route path="/">
                    <Route
                      path="account"
                      element={<Account setIsPlainPage={setIsPlainPage} />}
                    >
                      <Route index element={<AccountOverview />} />
                      <Route path="order-history">
                        <Route index element={<OrderHistory />} />
                        <Route path=":id" element={<OrderDetails />} />
                      </Route>
                      <Route path="settings">
                        <Route index element={<AccountSettings />} />
                        <Route path="details" element={<AccountDetails />} />
                        <Route path="addresses" element={<SavedAddresses />} />
                      </Route>
                    </Route>

                    <Route path="account">
                      <Route
                        path="addresses"
                        element={<Navigate to="/account/settings/addresses" />}
                      />
                      <Route
                        path="orders/*"
                        element={<Navigate to="/account/order-history" />}
                      />
                      <Route
                        path="login"
                        element={<Authentication initialStep={LOGIN} />}
                      />
                      <Route
                        path="register"
                        element={<Authentication initialStep={ACTIVATE} />}
                      />
                      <Route
                        path="reset/:id/:token"
                        element={<Authentication initialStep={FORGOT} />}
                      />
                      <Route
                        path="activate/:id/:token"
                        element={<Authentication initialStep={FORGOT} />}
                      />
                      <Route
                        path="invalid_token"
                        element={<Authentication initialStep={FORGOT} />}
                      />
                    </Route>

                    <Route path="pages">
                      <Route
                        path="forgot-password"
                        element={<Authentication initialStep={FORGOT} />}
                      />
                    </Route>

                    <Route path="cart" element={<Cart {...config?.Cart} />} />

                    <Route path="*" element={<NotFoundPageWrapper />} />
                  </Route>
                </Routes>
              </Loader>
            </Styled.ContentContainer>
            {/**
             * Footer is rendered into a portal because there can still be non-React
             * content included on the page that appears above it.
             * */}
            {createPortal(
              <Footer
                {...window?.[config?.Footer.globalConfigVar]}
                couponCode={siteConfig?.coupon_code?.value}
                isAutoApplied={siteConfig?.is_discount_auto_applied?.value}
                codeDiscountPercentage={Number(
                  siteConfig?.coupon_code_percentage?.value
                )}
              />,
              document.querySelector('#footer')
            )}
            <AuthenticationDialog />
            <CartDialog {...config?.CartDialog} />
            <CouponContainer
              couponCode={siteConfig?.coupon_code?.value}
              isAutoApplied={siteConfig?.is_discount_auto_applied?.value}
              smsTermsOfService={siteConfig?.sms_terms_of_service?.value}
              codeDiscountPercentage={Number(
                siteConfig?.coupon_code_percentage?.value
              )}
              viewToDisplay={viewCouponDialogDisplay}
              setViewToDisplay={setViewCouponDialogDisplay}
            />
            <AccountNavDialog />
            {loopParams && (
              <LoopNotification
                loopCredit={loopParams.loop_total}
                returnURL={loopParams.loop_redirect_url}
              />
            )}
          </Router>
        </RemoveScrollProvider>
      </AuthenticationProvider>
    </BreakpointProvider>
  );
};

App.displayName = 'App';

export default App;
